import { useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";

import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDButton from "components/MDButton";
import MediaPicker from "components/content/MediaPicker";
import { createAuthor, updateAuthor } from "api/endpoints/authors";

// Split a stored display name into first / last for the edit form. The list
// response only carries display_name, so an edited author is best-effort split.
const splitName = (author) => {
  if (author?.first_name || author?.last_name) {
    return { first: author.first_name || "", last: author.last_name || "" };
  }
  const parts = (author?.display_name || "").trim().split(/\s+/);
  return { first: parts[0] || "", last: parts.slice(1).join(" ") };
};

/**
 * Batch 2.2 — <AuthorForm>. Create / edit a CMS author. Used by the Authors
 * screen and, nested, by <AuthorSelect>'s "+ New author" action.
 */
function AuthorForm({ websiteId, author, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(author?.author_id);
  const initialName = splitName(author);

  const [form, setForm] = useState({
    first_name: initialName.first,
    last_name: initialName.last,
    email: author?.email || "",
    bio: author?.bio || "",
    profile_image: author?.profile_image || null,
    is_global: author?.is_global || false,
    is_active: author?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      enqueueSnackbar("First and last name are required", { variant: "warning" });
      return;
    }
    setSaving(true);
    try {
      let result;
      if (isEdit) {
        result = await updateAuthor(websiteId, author.author_id, {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email || undefined,
          bio: form.bio || undefined,
          profile_image: form.profile_image || undefined,
          is_active: form.is_active,
        });
      } else {
        result = await createAuthor(websiteId, {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email || undefined,
          bio: form.bio || undefined,
          profile_image: form.profile_image || undefined,
          is_global: form.is_global,
        });
      }
      enqueueSnackbar(isEdit ? "Author updated" : "Author created", { variant: "success" });
      onSaved(result);
    } catch {
      enqueueSnackbar("Failed to save author", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack component="form" spacing={3} onSubmit={handleSubmit} noValidate>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label="First name"
          value={form.first_name}
          onChange={set("first_name")}
          fullWidth
          required
        />
        <TextField
          label="Last name"
          value={form.last_name}
          onChange={set("last_name")}
          fullWidth
          required
        />
      </Stack>

      <TextField label="Email" type="email" value={form.email} onChange={set("email")} fullWidth />

      <TextField
        label="Bio"
        value={form.bio}
        onChange={set("bio")}
        fullWidth
        multiline
        minRows={3}
      />

      <MediaPicker
        label="Profile image"
        value={form.profile_image}
        onChange={(id) => setForm((prev) => ({ ...prev, profile_image: id }))}
        websiteId={websiteId}
        visibility="public"
        accept="image/*"
      />

      {isEdit ? (
        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            />
          }
          label="Active"
        />
      ) : (
        <FormControlLabel
          control={
            <Switch
              checked={form.is_global}
              onChange={(e) => setForm((prev) => ({ ...prev, is_global: e.target.checked }))}
            />
          }
          label="Global author (shared across all websites)"
        />
      )}

      <MDButton type="submit" color="info" variant="gradient" disabled={saving} fullWidth>
        {isEdit ? "Update Author" : "Create Author"}
      </MDButton>
    </Stack>
  );
}

AuthorForm.defaultProps = {
  websiteId: undefined,
  author: null,
};

AuthorForm.propTypes = {
  websiteId: PropTypes.string,
  author: PropTypes.shape({
    author_id: PropTypes.string,
    display_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    bio: PropTypes.string,
    profile_image: PropTypes.string,
    is_global: PropTypes.bool,
    is_active: PropTypes.bool,
  }),
  onSaved: PropTypes.func.isRequired,
};

export default AuthorForm;
